import type { ChangeFormField, ChangeRequest } from '../../types/changes';
import { isEmergencyChangeType, labelChangeType } from './changeLabels';
import {
  formatDateTimeLabel,
  formatGoLiveWindow,
  formatGovernanceDate,
  formatGovernanceWindow,
} from './goLiveWindow';

export type DetailSummaryLayoutField = Pick<
  ChangeFormField,
  'id' | 'kind' | 'native_key' | 'label' | 'field_type' | 'col_span' | 'detail_summary_visible'
>;

const SAP_LABELS: Record<string, string> = {
  sap: 'SAP',
  nao_sap: 'Não SAP',
};

export function detailSummaryRowLabel(field: DetailSummaryLayoutField, change: ChangeRequest): string {
  const key = field.native_key || '';
  if (key === 'go_live_window') {
    return isEmergencyChangeType(change.change_type)
      ? 'Início e fim da implementação:'
      : 'Início e fim do Go Live:';
  }
  if (key === 'risk_committee_window') {
    return 'Agendamento Riscos e Impactos:';
  }
  if (key === 'camud_go_no_go_date') {
    return 'Agendamento CAMUD Go/No-Go:';
  }
  if (key === 'rollback_plan') {
    return 'Plano de Rollback:';
  }
  return `${field.label}:`;
}

export function shouldRenderDetailSummaryRow(
  field: DetailSummaryLayoutField,
  change: ChangeRequest,
): boolean {
  if (!field.detail_summary_visible) {
    return false;
  }
  const key = field.native_key || '';
  if (!change.scope && key === 'scope') {
    return false;
  }
  const value = formatDetailSummaryValue(field, change);
  if (field.kind === 'extra' && !value) {
    return false;
  }
  return true;
}

function formatExtraSummaryValue(
  field: DetailSummaryLayoutField,
  raw?: string | null,
): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (field.field_type === 'date') {
    return formatGovernanceDate(text);
  }
  if (field.field_type === 'datetime') {
    return formatDateTimeLabel(text);
  }
  if (field.field_type === 'period' || field.field_type === 'period_datetime') {
    try {
      const parsed = JSON.parse(text) as { start?: string; end?: string };
      const start = parsed.start || '';
      const end = parsed.end || '';
      if (!start && !end) return '';
      return field.field_type === 'period'
        ? formatGovernanceWindow(start, end)
        : formatGoLiveWindow(start, end);
    } catch {
      return text.includes('T') ? formatDateTimeLabel(text) : formatGovernanceDate(text);
    }
  }
  return text;
}

export function formatDetailSummaryValue(
  field: DetailSummaryLayoutField,
  change: ChangeRequest,
): string {
  const key = field.native_key || '';
  if (field.kind === 'extra') {
    return formatExtraSummaryValue(field, change.extra_fields?.[String(field.id)]);
  }
  switch (key) {
    case 'change_owner_id':
      return change.owner?.name || 'Não informado';
    case 'requester_id':
      return change.requester?.name || 'Não informado';
    case 'requester_area_id':
      return change.requester_area?.name || 'Não informada';
    case 'requester_manager_group_id':
      return (
        change.requester_immediate_manager_display ||
        change.requester_manager_group_name ||
        'Não selecionado'
      );
    case 'category_id':
      return change.category?.name || 'Não informada';
    case 'change_type':
      return labelChangeType(change.change_type);
    case 'project':
      return change.project || 'Não informado';
    case 'sap_classification': {
      const token = String(change.sap_classification || '').toLowerCase();
      return SAP_LABELS[token] || change.sap_classification || 'Não informado';
    }
    case 'go_live_window':
      return formatGoLiveWindow(change.go_live_start_at, change.go_live_end_at);
    case 'risk_committee_window':
      return formatGovernanceWindow(change.risk_committee_start_at, change.risk_committee_end_at);
    case 'camud_go_no_go_date':
      return formatGovernanceDate(change.camud_go_no_go_date);
    case 'description':
      return change.description || '';
    case 'scope':
      return change.scope || '';
    case 'impacted_users':
      return change.impacted_users || '';
    case 'rollback_plan':
      return change.rollback_plan || '';
    case 'title':
      return change.title || '';
    case 'task_type_ids':
      return '';
    default:
      return '';
  }
}

export function detailSummaryUsesFullWidth(field: DetailSummaryLayoutField): boolean {
  if (field.col_span === 12) {
    return true;
  }
  if (field.field_type === 'text_long') {
    return true;
  }
  const key = field.native_key || '';
  return key === 'description' || key === 'scope' || key === 'impacted_users' || key === 'rollback_plan';
}

export function visibleDetailSummaryFields(
  layout: DetailSummaryLayoutField[],
  change: ChangeRequest,
): DetailSummaryLayoutField[] {
  return layout.filter((field) => shouldRenderDetailSummaryRow(field, change));
}
